import { cloneDeep, merge } from "lodash";
// Deep import: the data-model barrel also pulls in the data-model context (react-query, canonicalizer).
import { createEmptyEventInstance } from "@/entities/data-model/eventInstance";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import { type FormValueEventInstance, type FormValues, RecordSubject } from "./types";

/**
 * The canonical "no proximity" value — used by the outbound mapper to clear
 * proximity in range mode and by the empty-year hygiene strip. This must
 * never carry the experiment default; it represents the absence of proximity.
 */
export const EMPTY_DATE_PROXIMITY: FormValueEventInstance["dateProximity"] = {
    year: null,
    month: null,
    day: null,
};

export const EVENT_DEFAULT_INSTANCE: FormValueEventInstance = {
    date: {
        year: null,
        month: null,
        day: null,
        endYear: null,
    },
    location: "",
    gpid: "",
    dateProximity: EMPTY_DATE_PROXIMITY,
    locationExactness: null,
};

/** Pins the shared empty-instance factory's result to `FormValueEventInstance` so form code can mutate its exactness. */
export const createEventDefaultInstance = (defaultYearProximity: number | null): FormValueEventInstance =>
    createEmptyEventInstance(defaultYearProximity);

export const EXACT_DATE_PROXIMITY: FormValueEventInstance["dateProximity"] = { year: 0, month: 0, day: 0 };

/** Shared by seeding (metadata converters) and add-row (ClassicForm) so "exact" is spelled the same way in both; returns a copy. */
export const applyExactEventDefaults = (
    instance: FormValueEventInstance,
    { exactDate, exactPlace }: { exactDate: boolean; exactPlace: boolean },
): FormValueEventInstance => ({
    ...instance,
    ...(exactDate && { dateProximity: { ...EXACT_DATE_PROXIMITY } }),
    ...(exactPlace && { locationExactness: LOCATION_EXACTNESS_SCOPE.ThisPlace }),
});

export const createEventInstance = ({
    exact,
    defaultYearProximity,
}: {
    exact: boolean;
    defaultYearProximity: number | null;
}): FormValueEventInstance =>
    applyExactEventDefaults(createEventDefaultInstance(defaultYearProximity), { exactDate: exact, exactPlace: exact });

export const DEFAULT_GIVEN_NAME_EXACTNESS: FormValues["name"]["givenNameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        initials: false,
    },
    isExact: false,
};

export const DEFAULT_SURNAME_EXACTNESS: FormValues["name"]["surnameExactness"] = {
    flags: {
        phonetic: false,
        similar: false,
        soundex: false,
    },
    isExact: false,
};

export const RELATION_DEFAULT_INSTANCE: FormValues["name"] = {
    givenName: "",
    surname: "",
    secondSurname: "",
    givenNameExactness: DEFAULT_GIVEN_NAME_EXACTNESS,
    surnameExactness: DEFAULT_SURNAME_EXACTNESS,
};

/** Shared by seeding and add-row for the same reason as `applyExactEventDefaults`; returns a copy. */
export const applyExactNameDefaults = (
    instance: FormValues["name"],
    { exactGivenName, exactSurname }: { exactGivenName: boolean; exactSurname: boolean },
): FormValues["name"] => ({
    ...instance,
    ...(exactGivenName && { givenNameExactness: { ...DEFAULT_GIVEN_NAME_EXACTNESS, isExact: true } }),
    ...(exactSurname && { surnameExactness: { ...DEFAULT_SURNAME_EXACTNESS, isExact: true } }),
});

export const createFamilyMemberInstance = ({ exact }: { exact: boolean }): FormValues["name"] =>
    applyExactNameDefaults(cloneDeep(RELATION_DEFAULT_INSTANCE), { exactGivenName: exact, exactSurname: exact });

const getRelationDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [cloneDeep(RELATION_DEFAULT_INSTANCE)] : [],
});

const getEventDefaultValue = (withDefaultInstances = false) => ({
    instances: withDefaultInstances ? [EVENT_DEFAULT_INSTANCE] : [],
    relation: "",
    eventName: "",
});

export const getEmptyFormValues = (
    {
        withDefaultInstances,
        formValuePartials,
    }: {
        withDefaultInstances?: boolean;
        formValuePartials?: Partial<FormValues>;
    } = { withDefaultInstances: false },
): FormValues => {
    let formValues: FormValues = {
        name: {
            givenName: "",
            surname: "",
            secondSurname: "",
            givenNameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    initials: false,
                },
                isExact: false,
            },
            surnameExactness: {
                flags: {
                    phonetic: false,
                    similar: false,
                    soundex: false,
                },
                isExact: false,
            },
        },
        family: {
            father: getRelationDefaultValue(withDefaultInstances),
            mother: getRelationDefaultValue(withDefaultInstances),
            parent: getRelationDefaultValue(withDefaultInstances),
            spouse: getRelationDefaultValue(withDefaultInstances),
            child: getRelationDefaultValue(withDefaultInstances),
            sibling: getRelationDefaultValue(withDefaultInstances),
        },
        events: {
            SelfBirth: getEventDefaultValue(withDefaultInstances),
            SelfMarriage: getEventDefaultValue(withDefaultInstances),
            SelfDeath: getEventDefaultValue(withDefaultInstances),
            SelfResidence: getEventDefaultValue(withDefaultInstances),
            SelfMilitary: getEventDefaultValue(withDefaultInstances),
            SelfDeparture: getEventDefaultValue(withDefaultInstances),
            SelfArrival: getEventDefaultValue(withDefaultInstances),
            SelfDivorce: getEventDefaultValue(withDefaultInstances),
            SelfBaptism: getEventDefaultValue(withDefaultInstances),
            SelfBurial: getEventDefaultValue(withDefaultInstances),
            Self: getEventDefaultValue(withDefaultInstances),
        },
        treePerson: {
            treeId: "",
            personId: "",
        },
        types: {
            trees: true,
            records: true,
            photos: true,
            stories: true,
        },
        priority: "default",
        location: [],
        fields: {},
        recordSubject: RecordSubject.All,
        showUnviewedRecordsOnly: false,
        timespan: {
            yearStart: null,
            yearEnd: null,
            matchMode: "rankOnly",
            isVisible: false,
        },
    };

    if (formValuePartials) {
        formValues = merge(formValues, formValuePartials);
    }

    return formValues;
};
